import { Injectable } from '@angular/core';

export type FilterStateValue = Record<string, any>;

export interface FilterState<T extends FilterStateValue> {
  initial: T;
  applied: T;
  draft: T;
}

export interface TableViewState {
  first: number;
  rows?: number;
  sortField?: string | string[] | null;
  sortOrder?: number;
  multiSortMeta?: { field: string; order: number }[];
}

/**
 * Clone filter state at component boundaries so forms, services, and child
 * components never share mutable arrays, objects, or Date instances.
 */
export function cloneFilterState<T>(value: T): T {
  if (value instanceof Date) {
    return new Date(value.getTime()) as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) => cloneFilterState(item)) as T;
  }

  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).reduce((copy, key) => {
      (copy as Record<string, unknown>)[key] = cloneFilterState(
        (value as Record<string, unknown>)[key]
      );
      return copy;
    }, {} as T);
  }

  return value;
}

@Injectable({
  providedIn: 'root'
})
export class DataFilterService {
  create<T extends FilterStateValue>(initial: T): FilterState<T> {
    const snapshot = cloneFilterState(initial);
    return {
      initial: snapshot,
      applied: cloneFilterState(snapshot),
      draft: cloneFilterState(snapshot)
    };
  }

  open<T extends FilterStateValue>(state: FilterState<T>): FilterState<T> {
    return {
      ...state,
      draft: cloneFilterState(state.applied)
    };
  }

  submit<T extends FilterStateValue>(state: FilterState<T>, draft: T = state.draft): FilterState<T> {
    const applied = cloneFilterState(draft);
    return {
      ...state,
      applied,
      draft: cloneFilterState(applied)
    };
  }

  cancel<T extends FilterStateValue>(state: FilterState<T>): FilterState<T> {
    return this.open(state);
  }

  clear<T extends FilterStateValue>(
    state: FilterState<T>,
    preserveKeys: (keyof T)[] = []
  ): FilterState<T> {
    const cleared = preserveKeys.reduce((filter, key) => {
      if (Object.prototype.hasOwnProperty.call(state.applied, key)) {
        filter[key] = cloneFilterState(state.applied[key]);
      }
      return filter;
    }, {} as T);

    return this.submit(state, cleared);
  }

  remove<T extends FilterStateValue>(state: FilterState<T>, keys: (keyof T)[]): FilterState<T> {
    const applied = cloneFilterState(state.applied);
    keys.forEach((key) => delete applied[key]);
    return this.submit(state, applied);
  }

  refresh<T extends FilterStateValue>(state: FilterState<T>): FilterState<T> {
    return this.create(state.initial);
  }

  isActive(value: unknown): boolean {
    return value !== undefined &&
      value !== null &&
      value !== '' &&
      value !== 'All' &&
      (!Array.isArray(value) || value.length > 0);
  }

}
